import React, { useEffect, useState, useCallback } from "react";
import { toast } from "react-toastify";
import axiosInstance from "../../../axiosInstance";
import IconButton from "../../../components/IconButton/IconButton";
import "./EmailConfiguration.scss";
import GeneralTable from "../../../components/Table/GeneralTable";
import ColoredCheckbox from "../../../components/ColoredCheckbox/ColoredCheckbox";
import UpgradePage from "../../UpgradePlan";
import { Icon } from "@iconify/react";
import ButtonWithIcon from "../../../components/ButtonwithIcon/ButtonWithIcon";
import { useAuth } from "../../../contexts/AuthContext"; // Import useAuth

const EmailConfiguration = ({ markPageAsCompleted }) => {
  const [showForm, setShowForm] = useState(false);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    fromEmail: "",
    smtpHost: "",
    smtpPort: "",
    username: "",
    password: "",
    protocol: "",
    smtpAuth: false,
    startTlsEnable: false,
    sslEnable: false,
  });
  const [formErrors, setFormErrors] = useState({});
  const [emailConfig, setEmailConfig] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [hasAccess, setHasAccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const companyId = process.env.REACT_APP_COMPANY_ID;
  const { isAuthorized } = useAuth(); // Get isAuthorized function from AuthContext

  useEffect(() => {
    // Use the isAuthorized function to check for module access
    const hasEmailAutomationAccess = isAuthorized({
      module: "EMAIL_AUTOMATION",
    });
    setHasAccess(hasEmailAutomationAccess);
  }, [isAuthorized]);

  const fetchEmailConfig = useCallback(async () => {
    try {
      const response = await axiosInstance.get(
        `${process.env.REACT_APP_API_BASE_URL}/company/email-config/All`,
      );
      const data = response.data.data;
      setEmailConfig(data);

      if (response.data?.data && Object.keys(response.data.data).length > 0) {
        markPageAsCompleted("Email Configuration");
      }
    } catch (error) {
      console.error("Failed to fetch email configs:", error);
    }
  }, [markPageAsCompleted]);

  useEffect(() => {
    if (hasAccess) {
      fetchEmailConfig();
    }
  }, [hasAccess, fetchEmailConfig]);

  const resetForm = () => {
    setFormData({
      fromEmail: "",
      smtpHost: "",
      smtpPort: "",
      username: "",
      password: "",
      protocol: "",
      smtpAuth: false,
      startTlsEnable: false,
      sslEnable: false,
    });
    setIsEditing(false);
    setEditingId(null);
    setShowPassword(false);
  };

  const columns = [
    { header: "From Email", accessor: "fromEmail" },
    { header: "SMTP Host", accessor: "smtpHost" },
    { header: "SMTP Port", accessor: "smtpPort" },
    { header: "Username", accessor: "username" },
    { header: "Protocol", accessor: "protocol" },
    { header: "Actions", accessor: "actions" },
  ];

  const data = emailConfig.map((config) => ({
    fromEmail: config.fromEmail || "N/A",
    smtpHost: config.smtpHost || "N/A",
    smtpPort: config.smtpPort || "N/A",
    username: config.username || "N/A",
    protocol: config.protocol || "N/A",
    actions: (
      <div className="action-container">
        <IconButton
          icon="mdi:pencil-outline"
          onClick={() => handleEditRow(config)}
          variant={"edit"}
          title="Edit Config"
        />
        <IconButton
          icon="mdi:delete"
          onClick={() => handleDelete(config.emailConfigid)}
          variant={"delete"}
          title="Delete Config"
        />
      </div>
    ),
  }));

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const validateForm = () => {
    let isValid = true;
    const errors = {};
    if (!formData.fromEmail || !/\S+@\S+\.\S+/.test(formData.fromEmail)) {
      errors.fromEmail = "Enter valid Email";
      isValid = false;
    }
    if (!formData.smtpHost) {
      errors.smtpHost = "SMTP Host required";
      isValid = false;
    }
    if (!formData.smtpPort || isNaN(formData.smtpPort)) {
      errors.smtpPort = "SMTP Port required";
      isValid = false;
    }
    if (!formData.username) {
      errors.username = "Username is required";
      isValid = false;
    }
    if (!isEditing && !formData.password) {
      // Password is required only when not editing
      errors.password = "Password is required";
      isValid = false;
    }
    if (!formData.protocol) {
      errors.protocol = "Protocol is required";
      isValid = false;
    }
    setFormErrors(errors);
    return isValid;
  };

  const handleEditRow = async (config) => {
    try {
      const response = await axiosInstance.get(
        `${process.env.REACT_APP_API_BASE_URL}/company/email-config/${config.emailConfigid}`,
      );
      const fullData = response.data.data;
      setIsEditing(true);
      setEditingId(config.emailConfigid);
      setFormData({
        fromEmail: fullData.fromEmail || "",
        smtpHost: fullData.smtpHost || "",
        smtpPort: fullData.smtpPort || "",
        username: fullData.username || "",
        password: "", // Clear password on edit for security
        protocol: fullData.protocol || "",
        smtpAuth: fullData.smtpAuth || false,
        startTlsEnable: fullData.startTlsEnable || false,
        sslEnable: fullData.sslEnable || false,
      });
      setShowForm(true);
    } catch (error) {
      console.error("Failed to fetch email config:", error);
    }
  };

  const handleDelete = async (emailConfigId) => {
    try {
      await axiosInstance.delete(
        `${process.env.REACT_APP_API_BASE_URL}/company/email-config/${emailConfigId}`,
      );
      setEmailConfig((prevConfigs) =>
        prevConfigs.filter((config) => config.emailConfigid !== emailConfigId),
      );
      toast.success("Email configuration deleted successfully");
      fetchEmailConfig();
    } catch (error) {
      console.error("Failed to delete email config:", error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    const emailConfigData = {
      companyId,
      companyEmailConfigId: 0,
      ...formData,
    };
    try {
      if (isEditing) {
        const submitData = { ...emailConfigData };
        // Do not send password if it's empty during an update
        if (!submitData.password) {
          delete submitData.password;
        }
        await axiosInstance.put(
          `${process.env.REACT_APP_API_BASE_URL}/company/email-config/${editingId}`,
          submitData,
        );
        toast.success("Email configuration updated!");
      } else {
        await axiosInstance.post(
          `${process.env.REACT_APP_API_BASE_URL}/company/email-config`,
          emailConfigData,
        );
        toast.success("Email configuration created!");
      }
      resetForm();
      setShowForm(false);
      fetchEmailConfig();
      setError(null);
    } catch (err) {
      console.error("Failed to submit:", err);
      setError("Submission failed. Please try again.");
    }
  };

  if (!hasAccess) {
    return <UpgradePage featureName="Email Automation" />;
  }

  return (
    <div className="email-container">
      <h1 className="display6">Email Configuration</h1>
      {error && <div className="error-message">{error}</div>}
      <button
        className="create-role-button"
        onClick={() => {
          if (showForm) {
            resetForm();
          }
          setShowForm(!showForm);
        }}
        style={showForm ? { backgroundColor: "#ff6692", color: "#fff" } : {}}
      >
        {showForm ? (
          <>Close Email Configuration</>
        ) : (
          <>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={2}
              stroke="currentColor"
              className="icon"
              width="16"
              height="16"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 4v16m8-8H4"
              />
            </svg>
            Create Email Configuration
          </>
        )}
      </button>

      {showForm && (
        <div className="w-full p-3 bg-white rounded-2xl shadow-lg border border-gray-100 space-y-10">
          <h3 className="text-xl font-semibold text-gray-900 mb-1 ml-3 mt-2">
            {editingId !== null
              ? "Edit Email Configuration"
              : "Create New Email Configuration"}
          </h3>
          <form onSubmit={handleSubmit} className="email-form m-4">
            <div className="email-form-group">
              <label>From Email:</label>
              <input
                type="email"
                name="fromEmail"
                value={formData.fromEmail}
                onChange={handleChange}
                placeholder="example@company.com"
                required
              />
              {formErrors.fromEmail && (
                <div className="error-message">{formErrors.fromEmail}</div>
              )}
            </div>
            <div className="email-form-group">
              <label>SMTP Host:</label>
              <input
                type="text"
                name="smtpHost"
                value={formData.smtpHost}
                onChange={handleChange}
                placeholder="smtp.example.com"
                required
              />
              {formErrors.smtpHost && (
                <div className="error-message">{formErrors.smtpHost}</div>
              )}
            </div>
            <div className="email-form-group">
              <label>SMTP Port:</label>
              <input
                type="number"
                name="smtpPort"
                value={formData.smtpPort}
                onChange={handleChange}
                placeholder="587"
                required
              />
              {formErrors.smtpPort && (
                <div className="error-message">{formErrors.smtpPort}</div>
              )}
            </div>
            <div className="email-form-group">
              <label>Username:</label>
              <input
                type="text"
                name="username"
                value={formData.username}
                onChange={handleChange}
                placeholder="username@example.com"
                required
              />
              {formErrors.username && (
                <div className="error-message">{formErrors.username}</div>
              )}
            </div>
            <div className="email-form-group">
              <label>Password:</label>
              <div className="password-input-wrapper">
                <input
                  type={showPassword ? "text" : "password"}
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder={
                    isEditing
                      ? "Leave blank to keep current password"
                      : "••••••••"
                  }
                  required={!isEditing}
                />
                <span
                  className="password-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  <Icon icon={showPassword ? "mdi:eye-off" : "mdi:eye"} />
                </span>
              </div>
              {formErrors.password && (
                <div className="error-message">{formErrors.password}</div>
              )}
            </div>
            <div className="email-form-group">
              <label>Protocol:</label>
              <input
                type="text"
                name="protocol"
                value={formData.protocol}
                onChange={handleChange}
                placeholder="smtp"
                required
              />
              {formErrors.protocol && (
                <div className="error-message">{formErrors.protocol}</div>
              )}
            </div>
            <div className="checkbox-group mt-4">
              <ColoredCheckbox
                label="SMTP Authentication"
                color="green"
                name="smtpAuth"
                checked={formData.smtpAuth}
                onChange={handleChange}
              />
              <ColoredCheckbox
                label="Enable STARTTLS"
                color="green"
                name="startTlsEnable"
                checked={formData.startTlsEnable}
                onChange={handleChange}
              />
              <ColoredCheckbox
                label="Enable SSL"
                color="green"
                name="sslEnable"
                checked={formData.sslEnable}
                onChange={handleChange}
              />
            </div>
            <div className="ml-0 mt-6">
              <ButtonWithIcon
                type="submit"
                className="submit-button"
                icon="tdesign:send"
                text={
                  isEditing ? "Update Configuration" : "Create Configuration"
                }
              />
            </div>
          </form>
        </div>
      )}
      <GeneralTable
        columns={columns}
        data={data}
        title="Email Configuration"
        showPagination={true}
        initialPageSize={10}
      />
    </div>
  );
};

export default EmailConfiguration;
